import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  Tag,
  Link,
  History,
  Brain,
  X,
  Eye,
  Sparkles,
  Settings,
  FileQuestion
} from 'lucide-react';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../context/auth-context';
import { API_URL } from '../config/api';
import { GoogleTaxonomySearch, TaxonomyNode } from '../components/sector/GoogleTaxonomySearch';
import { CategoryPreviewTree } from '../components/sector/CategoryPreviewTree';

interface Sector {
  id: number;
  name: string;
  parentId: number | null;
  parentName?: string;
  level: number;
  path: string[];
  productKeywords: string[];
  urlPatterns: string[];
  productDescription?: string;
  autoLearned: boolean;
  keywordCount: number;
  patternCount: number;
  baselineFeatures?: {
    sector: string;
    products_or_services: Array<{
      type: string;
      baseline_features: string[];
    }>;
  };
}

interface SectorDetails {
  sector: {
    id: number;
    name: string;
    parentId: number | null;
    parentName?: string;
    productKeywords: string[];
    urlPatterns: string[];
    productDescription?: string;
    autoLearned: boolean;
    lastUpdated: string;
    google_taxonomy_root_id?: number;
    google_taxonomy_root_path?: string;
    baselineFeatures?: {
      sector: string;
      products_or_services: Array<{
        type: string;
        baseline_features: string[];
      }>;
    };
  };
  learningHistory: Array<{
    id: string;
    site_name: string;
    site_url: string;
    learned_keywords: string[];
    learned_patterns: string[];
    confidence: number;
    approved: boolean;
    created_at: string;
    created_by_email?: string;
  }>;
  recentSites: Array<{
    id: string;
    name: string;
    url: string;
    created_at: string;
  }>;
  statistics: {
    totalSites: number;
    totalKeywords: number;
    totalPatterns: number;
    learningEntries: number;
  };
}

export default function SectorManagementEnhanced() {
  const navigate = useNavigate();
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [sectorDetails, setSectorDetails] = useState<SectorDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteAllDataDialogOpen, setDeleteAllDataDialogOpen] = useState(false);
  const [deletingAllData, setDeletingAllData] = useState(false);
  const [processingTopics, setProcessingTopics] = useState<string[]>([]);

  // Google Taxonomy integration state
  const [selectedTaxonomyNode, setSelectedTaxonomyNode] = useState<TaxonomyNode | null>(null);
  const [showCategoryPreview, setShowCategoryPreview] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<{
    percentage: number;
    inserted: number;
    total: number;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Schema.org suggestion state
  const [schemaOrgSuggestion, setSchemaOrgSuggestion] = useState<{
    schema_org_type: string;
    confidence: number;
    reasoning: string;
    alternatives: string[];
    is_product_focused: boolean;
    is_service_focused: boolean;
    schema_org_url: string;
  } | null>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [selectedSchemaType, setSelectedSchemaType] = useState<string>('');

  // Form state for editing
  const [editFormData, setEditFormData] = useState({
    sectorName: '',
    parentId: null as number | null,
    productKeywords: [] as string[],
    urlPatterns: [] as string[],
    productDescription: '',
    newKeyword: '',
    newPattern: '',
    selectedTaxonomyNode: null as TaxonomyNode | null
  });

  // Form state for creating new sector
  const [createFormData, setCreateFormData] = useState({
    sector_name: '',
    description: '',
    parent_sector_id: ''
  });

  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/sector-management/sectors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch sectors');
      }
      
      const data = await response.json();
      setSectors(data);
    } catch (error) {
      console.error('Error loading sectors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sectors',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSectorDetails = async (sectorId: number) => {
    setDetailsLoading(true);
    try {
      const response = await fetch(`${API_URL}/sector-management/sectors/${sectorId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sector details');
      }

      const data = await response.json();
      setSectorDetails(data);
      return data; // Return the data so it can be used immediately
    } catch (error) {
      console.error('Error loading sector details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sector details',
        variant: 'destructive',
      });
      return null;
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSectorSelect = (sector: Sector) => {
    setSelectedSector(sector);
    loadSectorDetails(sector.id);
    setDetailsDialogOpen(true);
  };

  const handleCreate = () => {
    setCreateFormData({ sector_name: '', description: '', parent_sector_id: '' });
    setCreateDialogOpen(true);
  };

  const submitCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/sectors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: createFormData.sector_name,
          description: createFormData.description || null,
          parent_sector_id: createFormData.parent_sector_id || null
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create sector');
      }

      toast({
        title: 'Success',
        description: 'Sector created successfully',
      });

      setCreateDialogOpen(false);
      loadSectors();
    } catch (error) {
      console.error('Error creating sector:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create sector',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = async (sectorToEdit?: Sector) => {
    // If sector is provided, load its details first
    let details = sectorDetails;
    if (sectorToEdit) {
      setSelectedSector(sectorToEdit);
      const loadedDetails = await loadSectorDetails(sectorToEdit.id);
      if (!loadedDetails) {
        return; // Failed to load details
      }
      details = loadedDetails; // Use the fresh data
    }

    if (!details) {
      console.error('No sector details available');
      return;
    }

    // Load taxonomy info if sector has one
    let taxonomyNode: TaxonomyNode | null = null;
    if (details.sector.google_taxonomy_root_id) {
      try {
        const response = await fetch(
          `${API_URL}/google-taxonomy/node/${details.sector.google_taxonomy_root_id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            taxonomyNode = data.data;
          }
        }
      } catch (error) {
        console.error('Error loading taxonomy node:', error);
      }
    }

    setEditFormData({
      sectorName: details.sector.name,
      parentId: details.sector.parentId,
      productKeywords: [...details.sector.productKeywords],
      urlPatterns: [...details.sector.urlPatterns],
      productDescription: details.sector.productDescription || '',
      newKeyword: '',
      newPattern: '',
      selectedTaxonomyNode: taxonomyNode
    });
    setSelectedTaxonomyNode(taxonomyNode);
    setEditDialogOpen(true);
  };

  const handleAddKeyword = () => {
    if (editFormData.newKeyword.trim()) {
      setEditFormData({
        ...editFormData,
        productKeywords: [...editFormData.productKeywords, editFormData.newKeyword.trim()],
        newKeyword: ''
      });
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setEditFormData({
      ...editFormData,
      productKeywords: editFormData.productKeywords.filter((_, i) => i !== index)
    });
  };

  const handleAddPattern = () => {
    if (editFormData.newPattern.trim()) {
      setEditFormData({
        ...editFormData,
        urlPatterns: [...editFormData.urlPatterns, editFormData.newPattern.trim()],
        newPattern: ''
      });
    }
  };

  const handleRemovePattern = (index: number) => {
    setEditFormData({
      ...editFormData,
      urlPatterns: editFormData.urlPatterns.filter((_, i) => i !== index)
    });
  };

  // Schema.org Suggestion Handler
  const fetchSchemaOrgSuggestion = async () => {
    if (!selectedSector) return;

    setLoadingSuggestion(true);
    setSchemaOrgSuggestion(null);

    try {
      const response = await fetch(
        `${API_URL}/sectors/${selectedSector.id}/suggest-schema-org`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get Schema.org suggestion');
      }

      const data = await response.json();

      if (data.success) {
        setSchemaOrgSuggestion(data.suggestion);
        setSelectedSchemaType(data.suggestion.schema_org_type);
        toast({
          title: 'AI Suggestion Ready',
          description: `Suggested: ${data.suggestion.schema_org_type} (${Math.round(data.suggestion.confidence * 100)}% confidence)`,
        });
      } else {
        throw new Error(data.error || 'Suggestion failed');
      }
    } catch (error) {
      console.error('Schema.org suggestion error:', error);
      toast({
        title: 'Error',
        description: 'Failed to get Schema.org suggestion',
        variant: 'destructive',
      });
    } finally {
      setLoadingSuggestion(false);
    }
  };

  // Import workflow handlers
  const handleStartImport = async () => {
    if (!selectedTaxonomyNode || !selectedSector) return;

    setIsImporting(true);
    setShowCategoryPreview(false);

    try {
      const response = await fetch(
        `${API_URL}/google-taxonomy/sectors/${selectedSector.id}/import-taxonomy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            rootTaxonomyId: selectedTaxonomyNode.id,
            maxDepth: null,
            dryRun: false,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed to start import');

      const data = await response.json();
      if (data.success) {
        const jobId = data.data.job_id;
        setImportJobId(jobId);

        // Start polling
        const pollInterval = setInterval(async () => {
          const shouldStop = await pollImportStatus(jobId);
          if (shouldStop) clearInterval(pollInterval);
        }, 3000);

        toast({
          title: 'Import Started',
          description: 'Categories are being imported in the background.',
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      setIsImporting(false);
      toast({
        title: 'Error',
        description: 'Failed to start category import',
        variant: 'destructive',
      });
    }
  };

  const pollImportStatus = async (jobId: string): Promise<boolean> => {
    if (!selectedSector) return true;

    try {
      const response = await fetch(
        `${API_URL}/google-taxonomy/sectors/${selectedSector.id}/import-status/${jobId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      if (!response.ok) return true;

      const data = await response.json();
      if (data.success) {
        const job = data.data;

        if (job.progress) {
          setImportProgress({
            percentage: job.progress.percentage || 0,
            inserted: job.progress.inserted || 0,
            total: job.progress.total || 0,
          });
        }

        if (job.status === 'completed') {
          setIsImporting(false);
          setImportProgress(null);
          setSelectedTaxonomyNode(null);

          toast({
            title: 'Import Complete!',
            description: `Successfully imported ${job.progress.inserted} categories`,
          });

          await loadSectors(); // Refresh sector list
          return true;
        } else if (job.status === 'failed') {
          setIsImporting(false);
          setImportProgress(null);

          toast({
            title: 'Import Failed',
            description: job.error_message || 'Import job failed',
            variant: 'destructive',
          });

          return true;
        }

        return false; // Continue polling
      }

      return true;
    } catch (error) {
      console.error('Poll status error:', error);
      return true;
    }
  };

  const handleCancelImport = () => {
    setSelectedTaxonomyNode(null);
    setShowCategoryPreview(false);
    setIsImporting(false);
    setImportProgress(null);
    setImportJobId(null);
  };

  const submitEdit = async () => {
    if (!selectedSector) return;

    try {
      const updatePayload: any = {
        productKeywords: editFormData.productKeywords,
        urlPatterns: editFormData.urlPatterns,
        productDescription: editFormData.productDescription || null
      };

      // Add name if changed
      if (editFormData.sectorName && editFormData.sectorName !== selectedSector.name) {
        updatePayload.name = editFormData.sectorName;
      }

      // Add parent if changed
      if (editFormData.parentId !== selectedSector.parentId) {
        updatePayload.parent_sector_id = editFormData.parentId;
      }

      // Add taxonomy if changed
      if (editFormData.selectedTaxonomyNode) {
        updatePayload.google_taxonomy_root_id = editFormData.selectedTaxonomyNode.id;
        updatePayload.google_taxonomy_root_path = editFormData.selectedTaxonomyNode.path;
      }

      // Add Schema.org type if selected
      if (selectedSchemaType) {
        updatePayload.schema_org_type = selectedSchemaType;
        updatePayload.schema_org_url = `https://schema.org/${selectedSchemaType}`;
      }

      const response = await fetch(`${API_URL}/sector-management/sectors/${selectedSector.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update sector');
      }

      toast({
        title: 'Success',
        description: 'Sector updated successfully',
      });

      setEditDialogOpen(false);
      loadSectors();
      if (selectedSector) {
        loadSectorDetails(selectedSector.id);
      }
    } catch (error) {
      console.error('Error updating sector:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update sector',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedSector) return;

    try {
      const response = await fetch(`${API_URL}/sector-management/sectors/${selectedSector.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete sector');
      }

      toast({
        title: 'Success',
        description: 'Sector deleted successfully',
      });

      setDeleteDialogOpen(false);
      setSelectedSector(null);
      setSectorDetails(null);
      loadSectors();
    } catch (error) {
      console.error('Error deleting sector:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete sector',
        variant: 'destructive',
      });
    }
  };

  const approveLearning = async (historyId: string) => {
    if (!selectedSector) return;

    try {
      const response = await fetch(
        `${API_URL}/sector-management/sectors/${selectedSector.id}/learning/${historyId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve learning');
      }

      toast({
        title: 'Success',
        description: 'Learning approved and applied to sector',
      });

      loadSectorDetails(selectedSector.id);
    } catch (error) {
      console.error('Error approving learning:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve learning',
        variant: 'destructive',
      });
    }
  };

  const generateBaselineFeatures = async (sectorId: number) => {
    try {
      const response = await fetch(
        `${API_URL}/sectors/${sectorId}/generate-baseline-features`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate baseline features');
      }
      
      toast({
        title: 'Success',
        description: 'Baseline features generated successfully',
      });
      
      loadSectorDetails(sectorId);
    } catch (error) {
      console.error('Error generating baseline features:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate baseline features',
        variant: 'destructive',
      });
    }
  };

  const refreshSectorResearch = async (sectorId: number) => {
    setResearchLoading(true);
    try {
      toast({
        title: 'Processing',
        description: 'Generating comprehensive sector research. This may take a minute...',
      });

      const response = await fetch(
        `${API_URL}/sector-research/${sectorId}/generate-complete`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh sector research');
      }

      const result = await response.json();

      toast({
        title: 'Success',
        description: `Generated ${result.data?.product_keywords?.length || 0} keywords, ${result.data?.url_patterns?.length || 0} patterns, and ${result.data?.buyer_personas?.length || 0} personas`,
      });

      // Reload the sectors list and details
      loadSectors();
      loadSectorDetails(sectorId);
    } catch (error) {
      console.error('Error refreshing sector research:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to refresh sector research',
        variant: 'destructive',
      });
    } finally {
      setResearchLoading(false);
    }
  };

  const triggerTopicGeneration = async (sectorId: number) => {
    const sectorIdStr = sectorId.toString();
    setProcessingTopics([...processingTopics, sectorIdStr]);

    toast({
      title: 'Generating Topics',
      description: 'This may take 30-60 seconds...',
    });

    try {
      const response = await fetch(`${API_URL}/sectors/${sectorId}/discover-adjacent-topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate topics');
      }

      const result = await response.json();

      toast({
        title: 'Success! 🎉',
        description: `Generated ${result.topicsGenerated} topics for ${result.sector}. Check Master Topics Approval to review and approve them.`,
      });
    } catch (error) {
      console.error('Error generating topics:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate topics',
        variant: 'destructive',
      });
    } finally {
      setProcessingTopics(processingTopics.filter(id => id !== sectorIdStr));
    }
  };

  const generateAllTopics = async () => {
    if (sectors.length === 0) {
      toast({
        title: 'No Sectors',
        description: 'Please create sectors first before generating topics.',
        variant: 'destructive',
      });
      return;
    }

    const confirm = window.confirm(
      `This will generate topics for all ${sectors.length} sectors. This may take several minutes. Continue?`
    );

    if (!confirm) return;

    toast({
      title: 'Generating Topics for All Sectors',
      description: `Processing ${sectors.length} sectors. This may take several minutes...`,
    });

    let successCount = 0;
    let failCount = 0;

    for (const sector of sectors) {
      try {
        const sectorIdStr = sector.id.toString();
        setProcessingTopics(prev => [...prev, sectorIdStr]);

        const response = await fetch(`${API_URL}/sectors/${sector.id}/discover-adjacent-topics`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
        console.error(`Error generating topics for ${sector.name}:`, error);
      } finally {
        const sectorIdStr = sector.id.toString();
        setProcessingTopics(prev => prev.filter(id => id !== sectorIdStr));
      }
    }

    toast({
      title: 'Batch Generation Complete',
      description: `Successfully generated topics for ${successCount} sectors. ${failCount > 0 ? `${failCount} failed.` : ''} Check Master Topics Approval to review.`,
    });
  };

  const handleDeleteAllSectorData = async () => {
    if (!selectedSector) return;

    setDeletingAllData(true);
    try {
      const response = await fetch(
        `${API_URL}/sector-management/sectors/${selectedSector.id}/all-data`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409 && data.sites) {
          // Sites are using this sector
          toast({
            title: 'Cannot Delete Sector Data',
            description: `${data.sites.length} site(s) are using this sector. Remove them first.`,
            variant: 'destructive',
          });
        } else {
          throw new Error(data.error || 'Failed to delete sector data');
        }
        return;
      }

      toast({
        title: 'Success',
        description: `Deleted ${data.totalDeleted} records from sector "${selectedSector.name}"`,
      });

      setDeleteAllDataDialogOpen(false);
      loadSectorDetails(selectedSector.id);
    } catch (error) {
      console.error('Error deleting sector data:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete sector data',
        variant: 'destructive',
      });
    } finally {
      setDeletingAllData(false);
    }
  };

  const renderSectorHierarchy = () => {
    const rootSectors = sectors.filter(s => !s.parentId);
    
    const buildHierarchyPath = (sector: Sector): string => {
      if (!sector.parentId) return '';
      
      const parent = sectors.find(s => s.id === sector.parentId);
      if (!parent) return '';
      
      const parentPath = buildHierarchyPath(parent);
      return parentPath ? `${parentPath} → ${parent.name}` : parent.name;
    };
    
    const renderSectorRow = (sector: Sector, indent: number = 0) => {
      const childSectors = sectors.filter(s => s.parentId === sector.id);
      const hierarchyPath = buildHierarchyPath(sector);
      
      return (
        <React.Fragment key={sector.id}>
          <TableRow className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <TableCell>
              <div className="flex items-center" style={{ paddingLeft: `${indent * 20}px` }}>
                {childSectors.length > 0 && <ChevronRight className="h-4 w-4 mr-1" />}
                <span className="font-medium">{sector.name}</span>
              </div>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {hierarchyPath || '—'}
            </TableCell>
            <TableCell className="max-w-md">
              <p className="text-sm text-muted-foreground truncate">
                {sector.productDescription || '—'}
              </p>
            </TableCell>
            <TableCell>
              <div className="flex gap-1 flex-wrap">
                {sector.keywordCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Tag className="h-3 w-3 mr-1" />
                    {sector.keywordCount}
                  </Badge>
                )}
                {sector.patternCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Link className="h-3 w-3 mr-1" />
                    {sector.patternCount}
                  </Badge>
                )}
                {sector.baselineFeatures && (
                  <Badge variant="default" className="text-xs">
                    <Brain className="h-3 w-3 mr-1" />
                    Features
                  </Badge>
                )}
                {sector.autoLearned && (
                  <Badge variant="outline" className="text-xs">
                    Auto
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSectorSelect(sector)}
                  title="View details"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/research/question-wheel?sector=${sector.id}`);
                  }}
                  title="View question wheels"
                >
                  <FileQuestion className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerTopicGeneration(sector.id);
                  }}
                  disabled={processingTopics.includes(sector.id.toString())}
                  title="Generate topics for this sector"
                >
                  {processingTopics.includes(sector.id.toString()) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sectors/${sector.id}/generate-from-category`);
                  }}
                  title="Generate from product category"
                >
                  <Play className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sectors/${sector.id}/operational-tools`);
                  }}
                  title="Operational tools"
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(sector);
                  }}
                  title="Edit sector"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSector(sector);
                    setDeleteDialogOpen(true);
                  }}
                  title="Delete sector"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
          {childSectors.map(child => renderSectorRow(child, indent + 1))}
        </React.Fragment>
      );
    };
    
    return rootSectors.map(sector => renderSectorRow(sector));
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Sector Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage business sectors and their characteristics for intelligent website analysis
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Sector
            </Button>
            <Button
              variant="secondary"
              onClick={generateAllTopics}
              disabled={sectors.length === 0 || processingTopics.length > 0}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Topics for All Sectors
            </Button>
          </div>
        </div>
      </div>

      {/* Full-width Sectors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Business Sectors</CardTitle>
          <CardDescription>
            Click on a sector to view its baseline features and configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Name</TableHead>
                <TableHead className="w-[250px]">Parent Hierarchy</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[150px]">Features</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : sectors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No sectors found
                  </TableCell>
                </TableRow>
              ) : (
                renderSectorHierarchy()
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Sector Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedSector && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedSector.name}</DialogTitle>
                <DialogDescription className="text-base">
                  {sectorDetails?.sector.parentName && (
                    <span>Parent: {sectorDetails.sector.parentName} • </span>
                  )}
                  {sectorDetails?.statistics.totalSites || 0} sites using this sector
                </DialogDescription>
              </DialogHeader>

              {detailsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </div>
              ) : sectorDetails ? (
                <div className="space-y-6">
                  {/* Description */}
                  {sectorDetails.sector.productDescription && (
                    <div>
                      <h3 className="font-semibold mb-2">Sector Description</h3>
                      <p className="text-muted-foreground">
                        {sectorDetails.sector.productDescription}
                      </p>
                    </div>
                  )}

                  {/* Baseline Features */}
                  <div>
                    <h3 className="font-semibold mb-2">Customer Baseline Features</h3>
                    {sectorDetails.sector.baselineFeatures?.products_or_services ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Common features customers evaluate when choosing products/services in this sector:
                        </p>
                        {sectorDetails.sector.baselineFeatures.products_or_services.map((product, i) => (
                          <Card key={i}>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base">{product.type}</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex flex-wrap gap-2">
                                {product.baseline_features.map((feature, j) => (
                                  <Badge key={j} variant="secondary">
                                    {feature.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-4">
                          No baseline features have been generated for this sector yet.
                        </p>
                        <Button
                          onClick={() => generateBaselineFeatures(selectedSector.id)}
                          variant="outline"
                        >
                          <Brain className="h-4 w-4 mr-2" />
                          Generate Baseline Features
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Keywords and Patterns */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold">Product Keywords & URL Patterns</h3>
                      <Button
                        onClick={() => refreshSectorResearch(selectedSector.id)}
                        variant="outline"
                        size="sm"
                        disabled={researchLoading}
                      >
                        {researchLoading ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Brain className="h-4 w-4 mr-2" />Refresh Research</>
                        )}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Product Keywords</h4>
                        <div className="flex flex-wrap gap-2">
                          {sectorDetails.sector.productKeywords.length > 0 ? (
                            sectorDetails.sector.productKeywords.map((keyword, i) => (
                              <Badge key={i} variant="secondary">
                                {keyword}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">No keywords defined</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">URL Patterns</h4>
                        <div className="flex flex-wrap gap-2">
                          {sectorDetails.sector.urlPatterns.length > 0 ? (
                            sectorDetails.sector.urlPatterns.map((pattern, i) => (
                              <Badge key={i} variant="outline">
                                {pattern}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">No patterns defined</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Sites */}
                  {sectorDetails.recentSites.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Recent Sites</h3>
                      <div className="space-y-2">
                        {sectorDetails.recentSites.slice(0, 5).map((site) => (
                          <div key={site.id} className="flex justify-between items-center p-2 border rounded">
                            <div>
                              <p className="font-medium text-sm">{site.name}</p>
                              <p className="text-xs text-muted-foreground">{site.url}</p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {new Date(site.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Danger Zone - Super Admin Only */}
                  {user?.role === 'super_admin' && (
                    <div className="border-t pt-6 mt-6">
                      <Card className="border-red-200 bg-red-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-red-700 text-lg">
                            <AlertTriangle className="h-5 w-5" />
                            Danger Zone
                          </CardTitle>
                          <CardDescription className="text-red-600">
                            Destructive actions that cannot be undone
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-medium text-red-800">Delete All Sector Data</h4>
                              <p className="text-sm text-red-600 mt-1">
                                This will permanently delete all research data for this sector including:
                                personas, question wheels, category templates, benefit mappings, and all related research.
                              </p>
                              {sectorDetails.recentSites.length > 0 && (
                                <p className="text-sm text-red-700 font-medium mt-2">
                                  Warning: {sectorDetails.recentSites.length} site(s) are using this sector.
                                  Remove them first before deleting data.
                                </p>
                              )}
                            </div>
                            <Button
                              variant="destructive"
                              onClick={() => setDeleteAllDataDialogOpen(true)}
                              className="mt-2"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete All Sector Data
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ) : null}

              <DialogFooter className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedSector(sectorDetails?.sector || selectedSector);
                    handleEdit();
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Sector
                </Button>
                <Button onClick={() => setDetailsDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Sector</DialogTitle>
            <DialogDescription>
              Update sector name, taxonomy, keywords, and URL patterns
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sector Name */}
            <div>
              <Label>Sector Name</Label>
              <Input
                placeholder="Enter sector name..."
                value={editFormData.sectorName}
                onChange={(e) => setEditFormData({ ...editFormData, sectorName: e.target.value })}
                className="mt-2"
              />
            </div>

            {/* Parent Sector */}
            <div>
              <Label>Parent Sector (Optional)</Label>
              <Select
                value={editFormData.parentId?.toString() || 'none'}
                onValueChange={(value) => setEditFormData({
                  ...editFormData,
                  parentId: value === 'none' ? null : parseInt(value)
                })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select parent sector" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  <SelectItem value="none">None (Top-level sector)</SelectItem>
                  {sectors
                    .filter(s => selectedSector && s.id !== selectedSector.id) // Don't allow selecting itself
                    .map((sector) => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Google Taxonomy Selection */}
            <div className="border-t pt-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold">Google Product Taxonomy</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Select a category from Google's product taxonomy as the root for this sector
                </p>
              </div>

              <GoogleTaxonomySearch
                onSelect={(node) => {
                  setEditFormData({ ...editFormData, selectedTaxonomyNode: node });
                  setSelectedTaxonomyNode(node);
                  setShowCategoryPreview(true);
                }}
                initialValue={editFormData.selectedTaxonomyNode || undefined}
                disabled={isImporting}
              />

              {/* Category Preview & Import Controls */}
              {selectedTaxonomyNode && !isImporting && (
                <div className="mt-4">
                  <CategoryPreviewTree
                    rootNode={selectedTaxonomyNode}
                    onImport={handleStartImport}
                    onCancel={handleCancelImport}
                  />
                </div>
              )}

              {/* Import Progress Display */}
              {isImporting && importProgress && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <div>
                      <div className="font-medium text-blue-900">Importing Categories...</div>
                      <div className="text-sm text-blue-700">
                        {importProgress.inserted} of {importProgress.total} categories imported
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.percentage}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Schema.org Suggestion Section */}
            <div className="border-t pt-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  Schema.org Type (AI-Assisted)
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Let AI suggest the best Schema.org type for services, or choose manually
                </p>
              </div>

              {!schemaOrgSuggestion ? (
                <Button
                  variant="outline"
                  onClick={fetchSchemaOrgSuggestion}
                  disabled={loadingSuggestion || !editFormData.sectorName.trim()}
                  className="w-full"
                >
                  {loadingSuggestion ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Getting AI suggestion...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get Schema.org Suggestion
                    </>
                  )}
                </Button>
              ) : (
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {/* AI Suggestion */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                              AI Suggestion
                            </Badge>
                            <span className="text-sm font-semibold">{schemaOrgSuggestion.schema_org_type}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {Math.round(schemaOrgSuggestion.confidence * 100)}% confident
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {schemaOrgSuggestion.reasoning}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => setSelectedSchemaType(schemaOrgSuggestion.schema_org_type)}
                            variant={selectedSchemaType === schemaOrgSuggestion.schema_org_type ? "default" : "outline"}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Use This
                          </Button>
                          <a
                            href={schemaOrgSuggestion.schema_org_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View on Schema.org →
                          </a>
                        </div>
                      </div>

                      {/* Alternative Options */}
                      {schemaOrgSuggestion.alternatives && schemaOrgSuggestion.alternatives.length > 0 && (
                        <div className="border-t pt-3">
                          <Label className="text-xs text-muted-foreground mb-2 block">
                            Alternative Types
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {schemaOrgSuggestion.alternatives.map((alt) => (
                              <Button
                                key={alt}
                                size="sm"
                                variant={selectedSchemaType === alt ? "default" : "outline"}
                                onClick={() => setSelectedSchemaType(alt)}
                                className="text-xs"
                              >
                                {alt}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected Type Display */}
                      {selectedSchemaType && selectedSchemaType !== schemaOrgSuggestion.schema_org_type && (
                        <div className="bg-white rounded p-2 border">
                          <div className="text-xs font-semibold">Selected: {selectedSchemaType}</div>
                        </div>
                      )}

                      {/* Reset Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSchemaOrgSuggestion(null);
                          setSelectedSchemaType('');
                        }}
                        className="w-full text-xs"
                      >
                        Get New Suggestion
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Product Keywords & URL Patterns</h4>
              {/* Product Keywords */}
            <div>
              <Label>Product Keywords</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add keyword..."
                  value={editFormData.newKeyword}
                  onChange={(e) => setEditFormData({ ...editFormData, newKeyword: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                />
                <Button onClick={handleAddKeyword} size="sm">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {editFormData.productKeywords.map((keyword, i) => (
                  <Badge key={i} variant="secondary" className="pr-1">
                    {keyword}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-4 w-4 p-0 ml-1"
                      onClick={() => handleRemoveKeyword(i)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* URL Patterns */}
            <div>
              <Label>URL Patterns</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add pattern (e.g., /product/)..."
                  value={editFormData.newPattern}
                  onChange={(e) => setEditFormData({ ...editFormData, newPattern: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPattern()}
                />
                <Button onClick={handleAddPattern} size="sm">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {editFormData.urlPatterns.map((pattern, i) => (
                  <Badge key={i} variant="outline" className="pr-1">
                    {pattern}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-4 w-4 p-0 ml-1"
                      onClick={() => handleRemovePattern(i)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Product Description */}
            <div>
              <Label>Sector Description</Label>
              <Textarea
                placeholder="Brief description of what constitutes a product/service in this sector..."
                value={editFormData.productDescription}
                onChange={(e) => setEditFormData({ ...editFormData, productDescription: e.target.value })}
                rows={3}
                className="mt-2"
              />
            </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Sector</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSector?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Sector
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Sector Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Sector</DialogTitle>
            <DialogDescription>
              Add a new business sector to organize your content and topics.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="sector_name">Sector Name</Label>
              <Input
                id="sector_name"
                placeholder="e.g., Cycling Equipment, Running Gear"
                value={createFormData.sector_name}
                onChange={(e) => setCreateFormData({ ...createFormData, sector_name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this sector..."
                value={createFormData.description}
                onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="parent">Parent Sector (Optional)</Label>
              <Select
                value={createFormData.parent_sector_id || 'none'}
                onValueChange={(value) => setCreateFormData({ ...createFormData, parent_sector_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent sector" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  <SelectItem value="none">None</SelectItem>
                  {sectors.map((sector) => (
                    <SelectItem key={sector.id} value={sector.id.toString()}>
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitCreate} disabled={!createFormData.sector_name.trim()}>
              Create Sector
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Sector Data Confirmation Dialog */}
      <Dialog open={deleteAllDataDialogOpen} onOpenChange={setDeleteAllDataDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-red-600">Delete All Sector Data</DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              <p className="mb-3">
                This will permanently delete <strong>all research data</strong> for sector "{selectedSector?.name}":
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                <li>Sector personas and their images</li>
                <li>Question wheels and questions</li>
                <li>Category templates and benefit mappings</li>
                <li>Persona-benefit priority rankings</li>
                <li>Category perception research</li>
                <li>All other sector-specific research</li>
              </ul>
              <p className="mt-3 font-medium text-red-600">
                This action cannot be undone.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteAllDataDialogOpen(false)}
              disabled={deletingAllData}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAllSectorData}
              disabled={deletingAllData}
            >
              {deletingAllData ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}